import { useEffect, useMemo, useRef } from 'react';
import type { Message } from 'ably';
import { MessageCircle } from 'lucide-react';
import { MessageView } from '@/components/pages/live/ChatView';
import OfferCard from '@/components/common/asset/OfferCard';
import SurveyCard from '@/components/common/asset/SurveyCard';
import QuizCard from '@/components/common/asset/QuizCard';
import PopupBuyerCard from '@/components/common/asset/PopupBuyerCard';
import SharedResourceViewer from '@/components/pages/live/attendee/SharedResourceViewer';
import SendIcon from '@/components/icons/SendIcon';
import type {
  EvergreenChatMode,
  OfferTemplate,
  QuizTemplate,
  SharedResourceDownloadUrl,
  SharedResourcePublic,
  SurveyTemplate,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { EvergreenTestState } from '@/lib/evergreenTestTimeline';
import type { PurchaseNotificationOfferColors } from '@/lib/purchaseNotificationDisplay';
import { CHAT_MESSAGES_LIST_MAX } from '@/lib/chatListCap';
import { useWebinarT } from '@/hooks/useWebinarT';
import { cn } from '@/lib/utils';

export type EvergreenTestPurchaseNotification = {
  id: number;
  message: string;
  icon: string;
  showIcon: boolean;
  offerColors?: PurchaseNotificationOfferColors;
};

// Mêmes durées que le chat réel : plus courte quand d'autres notifications attendent.
const SHORT_NOTIFICATION_MS = 3000;
const FULL_NOTIFICATION_MS = 10000;

const noop = () => {};
const noopAsync = async () => {};

type EvergreenTestChatProps = {
  visualIdentity: WorkspaceVisualIdentity;
  state: EvergreenTestState;
  evergreenChatMode: EvergreenChatMode;
  offer: OfferTemplate | null;
  survey: SurveyTemplate | null;
  quiz: QuizTemplate | null;
  sharedResources: SharedResourcePublic[];
  onOfferClick: () => void;
  onSurveySubmit: (surveyId: number) => void;
  onQuizSubmit: (quizId: number) => void;
  requestDownloadUrl: (sharedResourceId: number) => Promise<SharedResourceDownloadUrl>;
  notification: EvergreenTestPurchaseNotification | null;
  hasMoreNotifications: boolean;
  onNotificationDone: () => void;
};

/**
 * Colonne de chat de la salle de test : même rendu que le chat spectateur (bulles, épinglés,
 * réactions, cartes desktop), mais en lecture seule et alimentée par le rejeu local.
 */
export default function EvergreenTestChat({
  visualIdentity,
  state,
  evergreenChatMode,
  offer,
  survey,
  quiz,
  sharedResources,
  onOfferClick,
  onSurveySubmit,
  onQuizSubmit,
  requestDownloadUrl,
  notification,
  hasMoreNotifications,
  onNotificationDone,
}: EvergreenTestChatProps) {
  const { t } = useWebinarT('webinars');
  const shownAtRef = useRef<{ id: number; at: number } | null>(null);

  useEffect(() => {
    if (!notification) return;
    if (shownAtRef.current?.id !== notification.id) {
      shownAtRef.current = { id: notification.id, at: Date.now() };
    }
    const target = hasMoreNotifications ? SHORT_NOTIFICATION_MS : FULL_NOTIFICATION_MS;
    const remaining = Math.max(0, target - (Date.now() - shownAtRef.current.at));
    const timer = window.setTimeout(onNotificationDone, remaining);
    return () => window.clearTimeout(timer);
  }, [notification, hasMoreNotifications, onNotificationDone]);

  const messages = useMemo(
    () =>
      state.messages.map(
        (message) =>
          ({ id: String(message.id), data: message.data, timestamp: message.atMs }) as Message,
      ),
    [state.messages],
  );
  const messagesById = useMemo(
    () => new Map(messages.map((message) => [message.id, message])),
    [messages],
  );
  const pinnedIds = useMemo(
    () => new Set(state.pinnedMessageIds.map(String)),
    [state.pinnedMessageIds],
  );
  const pinnedMessages = messages.filter((message) => pinnedIds.has(message.id ?? ''));
  const unpinnedMessages = messages
    .filter((message) => !pinnedIds.has(message.id ?? ''))
    .slice(-CHAT_MESSAGES_LIST_MAX);

  const isChatDisabled = state.chatMode === 'DISABLED' || evergreenChatMode === 'DISABLED';
  const composerTextClassName =
    visualIdentity.defaultIdentity === true
      ? visualIdentity.theme === 'LIGHT'
        ? 'text-black'
        : 'text-white'
      : visualIdentity.theme === 'LIGHT'
        ? 'text-tertiary'
        : 'text-tertiary-inverse';

  const renderMessage = (message: Message, isPinned: boolean) => {
    const { repliedToMessageId } = message.data as { repliedToMessageId: number | null };
    return (
      <MessageView
        key={message.id}
        message={message}
        isAdmin={false}
        isAttendee
        reactions={state.reactionsByMessageId[Number(message.id)]}
        onReact={noop}
        onPinMessage={noopAsync}
        isPinned={isPinned}
        onDeleteMessage={noop}
        repliedToMessage={
          repliedToMessageId != null ? messagesById.get(String(repliedToMessageId)) : undefined
        }
        visualIdentity={visualIdentity}
        isReactionDisabled
      />
    );
  };

  const purchaseNotificationCard = notification && (
    <div className="p-2">
      <PopupBuyerCard
        message={notification.message}
        icon={notification.icon}
        showIcon={notification.showIcon}
        visualIdentity={visualIdentity}
        onClose={onNotificationDone}
        offerColors={notification.offerColors}
      />
    </div>
  );

  return (
    <div
      className="h-full flex flex-col min-h-0 relative pb-0"
      style={{
        backgroundColor:
          visualIdentity.theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
      }}
    >
      {notification && purchaseNotificationCard}

      {offer && (
        <div className="p-2 hidden compact-landscape:block lg:block">
          <OfferCard
            offer={offer}
            visualIdentity={visualIdentity}
            attendeeView
            onLocalClick={onOfferClick}
          />
        </div>
      )}

      {survey && (
        <div className="p-2 hidden compact-landscape:block lg:block">
          <SurveyCard
            survey={survey}
            visualIdentity={visualIdentity}
            isAttendee
            onLocalSubmit={() => onSurveySubmit(survey.id)}
          />
        </div>
      )}

      {quiz &&
        (quiz.enabledDuringLive === false || isChatDisabled ? (
          <div className="p-2 hidden compact-landscape:block lg:block">
            <QuizCard
              quiz={quiz}
              visualIdentity={visualIdentity}
              isAttendee
              onLocalSubmit={() => onQuizSubmit(quiz.id)}
            />
          </div>
        ) : (
          <div
            className="absolute inset-0 z-50 flex flex-col justify-end lg:justify-between"
            style={{
              backgroundColor: visualIdentity.theme === 'LIGHT' ? '#C5C5CA' : '#6D6E7A',
            }}
          >
            <div className="flex-1 hidden compact-landscape:flex lg:flex items-center justify-center px-4">
              <QuizCard
                quiz={quiz}
                visualIdentity={visualIdentity}
                isAttendee
                onLocalSubmit={() => onQuizSubmit(quiz.id)}
              />
            </div>
            <div className="w-full cursor-not-allowed relative flex items-center justify-center h-12 px-2 bg-white rounded-t-[20px]">
              <p className="text-tertiary text-base">{t('live.chat.quizDisabled')}</p>
              <div className="absolute right-4 flex items-center justify-center">
                <SendIcon size={24} className="text-tertiary" />
              </div>
            </div>
          </div>
        ))}

      {sharedResources.length > 0 && (
        <div className="p-2 hidden compact-landscape:block lg:block">
          <SharedResourceViewer
            webinarId={0}
            sessionIdentifier=""
            authorization=""
            visualIdentity={visualIdentity}
            resources={sharedResources}
            requestDownloadUrl={requestDownloadUrl}
          />
        </div>
      )}

      <div className="shrink-0 max-h-[33%] overflow-y-auto">
        {pinnedMessages.length > 0 && (
          <div className="space-y-1 py-2 shadow-2xl">
            {pinnedMessages.map((message) => renderMessage(message, true))}
          </div>
        )}
      </div>

      <div
        className="pt-2 flex-1 overflow-y-auto min-h-0 space-y-2 flex flex-col-reverse relative"
        style={{ scrollbarColor: 'currentColor transparent' }}
      >
        <div className="flex flex-col space-y-2 justify-end flex-1">
          {unpinnedMessages.length > 0 ? (
            unpinnedMessages.map((message) => (
              <div key={message.id} data-message-id={message.id}>
                {renderMessage(message, false)}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 w-full h-full">
              <MessageCircle size={28} className="text-tertiary" />
              <p className="text-tertiary text-base text-center font-bold">
                {t('live.chat.welcome')}
              </p>
              <p className="text-tertiary text-base">{t('live.chat.welcomeSubtitle')}</p>
            </div>
          )}
        </div>
      </div>

      <div className="pt-2 shrink-0 flex flex-col gap-1">
        <div
          className={cn(
            'w-full flex items-center justify-between overflow-auto px-4 rounded-t-[20px]',
            visualIdentity.defaultIdentity === true ? 'border-none' : 'border-t border-l border-r',
          )}
          style={{
            backgroundColor:
              visualIdentity.theme === 'LIGHT'
                ? '#FFFFFF'
                : visualIdentity.backgroundColor || '#FFFFFF',
            borderColor: visualIdentity.contrastColor || '#FFFFFF',
          }}
        >
          <p
            className={cn(
              'w-full h-16 py-2 flex items-center cursor-not-allowed',
              composerTextClassName,
            )}
          >
            {t('evergreenTest.chatDisabled')}
          </p>
          <span
            className="opacity-50"
            style={{ color: visualIdentity.contrastColor || '#FFFFFF' }}
            aria-hidden
          >
            <SendIcon size={24} />
          </span>
        </div>
      </div>
    </div>
  );
}
